import { useState, useMemo } from 'react';
import {
  Search,
  CheckCheck,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { NotificationList } from '@/components/notifications/notification-list';

import { transformRealToUI } from '@/lib/notification-utils';
import { useTenant } from '@/hooks';
import { useGetCurrentUserQuery } from '@/state/user';
import {
  useFetchNotificationsQuery,
  useMarkAllAsReadMutation
} from '@/state/notifications/notification-api';
import type { ApiErrorResponse } from '@/types';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';

export default function NotificationsPage() {
  const { tenant } = useTenant();
  const { data: user } = useGetCurrentUserQuery({ tenant });

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  // Real-time updates handled globally in Layout

  // Fetch real notifications
  const { data, isLoading, isFetching, refetch, error } = useFetchNotificationsQuery({
    tenant: tenant!,
    userId: user?.id || '',
    page,
    isRead: undefined // Show all since filters are removed
  }, { skip: !user?.id || !tenant });

  const [markAllAsRead] = useMarkAllAsReadMutation();

  const notifications = useMemo(() => {
    if (!data?.results) return [];
    return data.results.map(transformRealToUI);
  }, [data]);

const filteredNotifications = useMemo(() => {
  if (!searchQuery.trim()) return notifications;

  return notifications.filter(n => {
    const query = searchQuery.toLowerCase();
    return (
      n.title.toLowerCase().includes(query) ||
      n.message.toLowerCase().includes(query)
    );
  });
}, [notifications, searchQuery]);

  const unreadCount = data?.results?.filter(n => !n.is_read).length || 0;

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead({ tenant: tenant! }).unwrap();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const totalPages = Math.ceil((data?.count || 0) / 10); // Assuming 10 per page

  return (
    <div className="mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2 border-b border-border/40">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Notifications</h1>
          <p className="text-muted-foreground flex items-center gap-2">
            You have <span className="text-primary font-semibold">{unreadCount}</span> unread notifications
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
            className="rounded-xl border-primary/20 hover:bg-primary/5 hover:text-primary transition-all duration-200"
          >
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark all as read
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem className="rounded-lg" onClick={refetch}>
                <Loader2 className="mr-2 h-4 w-4" />
                Refresh
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Search Section */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search notifications..."
          className="pl-11 h-12 rounded-2xl bg-muted/20 border-transparent focus:border-primary/20 focus:ring-primary/10 transition-all duration-300"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <WithLoadingErrorWrapper
        loading={isLoading}
        isFetching={isFetching}
        onRetry={refetch}
        error={error as ApiErrorResponse}
      >

        {/* Notifications List */}
        <div className="bg-background/40 backdrop-blur-sm rounded-[32px] border border-border/40 p-2 min-h-[400px] relative">
          <NotificationList
            notifications={filteredNotifications}
            currentUserId={user?.id}
            onNotificationClick={(id) => {
              // In a real app, this would call a markAsRead mutation for the single ID
              console.log('Notification clicked:', id);
            }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between px-4 pb-4">
              <p className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </WithLoadingErrorWrapper>
    </div>
  );
}
